import React, { useState } from 'react';

import {
  Button,
  Divider,
  GridToolbar,
  Stack,
  Typography,
} from '@packages/fenrir/components';
import type { GridToolbarProps } from '@packages/fenrir/components';

import { ConfirmActionDialog } from './action-confirmation-dialog';
import { ExportAction, type ExportActionProps } from './export-action';

export type ActionConfirmationProps = {
  title?: string;
  description?: string | React.ReactNode;
  actionText?: string;
  cancelText?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  isDestructive?: boolean;
};

export type ToolbarAction = {
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  isDestructive?: boolean;
  onClick?: () => void;
  actionConfirmation?: ActionConfirmationProps;
};

type RowSelectionProps = {
  count?: number;
  label?: string;
  containerSx?: object;
};

interface ActionBarProps {
  actions?: ToolbarAction[];
  rowSelection?: RowSelectionProps;
}

export interface GridActionToolbarProps extends GridToolbarProps {
  exportProps?: ExportActionProps;
  actionBarProps?: ActionBarProps;
}

export const GridActionToolbar: React.FC<GridActionToolbarProps> = ({
  actionBarProps,
  exportProps,
  ...props
}) => {
  const { actions = [], rowSelection } = actionBarProps ?? {};

  const selectedRowsCount = rowSelection?.count ?? 0;
  const selectedRowsLabel =
    rowSelection?.label ??
    `${selectedRowsCount} row${selectedRowsCount === 1 ? '' : 's'} selected`;
  const containerSx = rowSelection?.containerSx;

  const [confirmAction, setConfirmAction] =
    useState<ActionConfirmationProps | null>(null);

  const handleActionClick = (action: ToolbarAction) => {
    if (action.actionConfirmation) {
      setConfirmAction({
        ...action.actionConfirmation,
        isDestructive: action?.isDestructive,
      });
    } else if (action.onClick) {
      action.onClick();
    }
  };

  return (
    <Stack>
      <Stack direction="row">
        <GridToolbar {...props} sx={{ flex: 'auto' }} />
        {exportProps && Object.keys(exportProps).length && (
          <ExportAction {...exportProps} />
        )}
      </Stack>

      {selectedRowsCount > 0 && (
        <Stack
          justifyContent="space-between"
          alignItems="center"
          direction="row"
          sx={{
            px: 2,
            py: 1,
            my: 1,
            backgroundColor:
              'rgba(var(--aw-palette-primary-mainChannel) / var(--aw-palette-action-selectedOpacity))',
            ...containerSx,
          }}
        >
          <Typography variant="body2">{selectedRowsLabel}</Typography>

          <Stack
            direction="row"
            spacing={1}
            divider={
              <Divider
                component="div"
                role="presentation"
                orientation="vertical"
                variant="middle"
                flexItem
              />
            }
          >
            {actions.map((action) => (
              <React.Fragment key={action.label}>
                <Button
                  color={action?.isDestructive ? 'error' : 'primary'}
                  variant="text"
                  type="button"
                  disabled={action?.disabled}
                  loading={action?.isLoading}
                  startIcon={action.icon}
                  onClick={() => handleActionClick(action)}
                >
                  {action.label}
                </Button>
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      )}

      {confirmAction && (
        <ConfirmActionDialog
          confirmAction={confirmAction}
          setConfirmAction={setConfirmAction}
        />
      )}
    </Stack>
  );
};
